/**
 * DiscussionNotes - Component to view and manage conversation notes
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  MessageSquare, Plus, Check, Edit2, Trash2, X, 
  ChevronDown, ChevronUp, Clock, CheckCircle, MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  discussed: { label: 'Discussed', color: 'bg-blue-100 text-blue-700', icon: MessageCircle },
  resolved: { label: 'Resolved', color: 'bg-mc-blue-pale text-mc-blue-deep', icon: CheckCircle }
};

const DiscussionNotes = ({ relationshipId, category = null, compact = false }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editingNote, setEditingNote] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [expanded, setExpanded] = useState(!compact);
  const [filterStatus, setFilterStatus] = useState(null);

  const userId = sessionStorage.getItem('user_id');

  useEffect(() => {
    if (relationshipId) {
      fetchNotes();
    }
  }, [relationshipId, category, filterStatus]);

  const fetchNotes = async () => {
    try {
      let url = `${API}/notes/${relationshipId}?user_id=${userId}`;
      if (category) url += `&category=${encodeURIComponent(category)}`;
      if (filterStatus) url += `&status=${filterStatus}`;
      
      const response = await axios.get(url);
      setNotes(response.data.notes || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notes:', error);
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;

    try {
      await axios.post(`${API}/notes?user_id=${userId}`, {
        relationship_id: relationshipId,
        category: category || 'General',
        content: newNote.trim()
      });
      
      toast.success('Note saved!');
      setNewNote('');
      setShowAddNote(false);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to save note');
    }
  };

  const handleUpdateNote = async (noteId, updates) => {
    try {
      await axios.put(`${API}/notes/${noteId}?user_id=${userId}`, updates);
      toast.success('Note updated!');
      setEditingNote(null);
      fetchNotes();
    } catch (error) {
      toast.error('Failed to update note');
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    
    try {
      await axios.delete(`${API}/notes/${noteId}?user_id=${userId}`);
      toast.success('Note deleted');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to delete note');
    }
  };

  const handleStatusChange = async (noteId, newStatus) => {
    await handleUpdateNote(noteId, { status: newStatus });
  };

  if (loading) {
    return (
      <div className="animate-pulse bg-stone-100 rounded-xl h-24" />
    );
  }

  // Compact view for embedding in InsightsPanel
  if (compact && !expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-stone-500" />
          <span className="text-sm text-stone-600">
            {notes.length} note{notes.length !== 1 ? 's' : ''} for this topic
          </span>
        </div>
        <ChevronDown className="w-4 h-4 text-stone-400" />
      </button>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-stone-100 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-stone-100">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-purple-600" />
          <h4 className="font-semibold text-stone-900">Discussion Notes</h4>
          {notes.length > 0 && (
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
              {notes.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Status Filter */}
          <select
            value={filterStatus || ''}
            onChange={(e) => setFilterStatus(e.target.value || null)}
            className="text-xs border border-stone-200 rounded-lg px-2 py-1"
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="discussed">Discussed</option>
            <option value="resolved">Resolved</option>
          </select>
          
          {compact && (
            <button
              onClick={() => setExpanded(false)}
              className="p-1 hover:bg-stone-100 rounded"
            >
              <ChevronUp className="w-4 h-4 text-stone-400" />
            </button>
          )}
        </div>
      </div>

      {/* Notes List */}
      <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
        {notes.length === 0 ? (
          <p className="text-sm text-stone-500 text-center py-4">
            No notes yet. Add one to track your discussion progress!
          </p>
        ) : (
          notes.map((note) => (
            <div 
              key={note.note_id} 
              className="bg-stone-50 rounded-lg p-3 space-y-2"
            >
              {editingNote === note.note_id ? (
                <div className="space-y-2">
                  <textarea
                    value={editContent}
                    onChange={(e) => setEditContent(e.target.value)}
                    className="w-full p-2 text-sm border border-stone-200 rounded-lg resize-none"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleUpdateNote(note.note_id, { content: editContent })}
                      className="px-3 py-1 bg-mc-blue-deep text-white text-xs rounded-lg"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => setEditingNote(null)}
                      className="px-3 py-1 bg-stone-200 text-stone-700 text-xs rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="text-sm text-stone-700 whitespace-pre-wrap">{note.content}</p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* Status Badge */}
                      <select
                        value={note.status}
                        onChange={(e) => handleStatusChange(note.note_id, e.target.value)}
                        className={`text-xs px-2 py-0.5 rounded-full border-0 cursor-pointer ${STATUS_CONFIG[note.status]?.color || 'bg-stone-100'}`}
                      >
                        <option value="open">Open</option>
                        <option value="discussed">Discussed</option>
                        <option value="resolved">Resolved</option>
                      </select>
                      
                      <span className="text-xs text-stone-400">
                        by {note.author_name}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          setEditingNote(note.note_id);
                          setEditContent(note.content);
                        }}
                        className="p-1 hover:bg-stone-200 rounded"
                        title="Edit"
                      >
                        <Edit2 className="w-3 h-3 text-stone-500" />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.note_id)}
                        className="p-1 hover:bg-red-100 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-3 h-3 text-red-500" />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add Note */}
      <div className="p-4 border-t border-stone-100">
        {showAddNote ? (
          <div className="space-y-2">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="What did you discuss? Any insights or action items?"
              className="w-full p-3 text-sm border border-stone-200 rounded-lg resize-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
              rows={3}
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleAddNote}
                disabled={!newNote.trim()}
                className="px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Save Note
              </button>
              <button
                onClick={() => {
                  setShowAddNote(false);
                  setNewNote('');
                }}
                className="px-4 py-2 bg-stone-200 text-stone-700 text-sm rounded-lg hover:bg-stone-300"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddNote(true)}
            className="w-full py-2 border-2 border-dashed border-stone-200 rounded-lg text-sm text-stone-500 hover:border-purple-300 hover:text-purple-600 transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Discussion Note
          </button>
        )}
      </div>
    </div>
  );
};

export default DiscussionNotes;
